import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { TourCard } from "@/components/TourCard";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Search, X, SlidersHorizontal } from "lucide-react";
import api from "@/lib/api";
import { useSEO } from "@/lib/seo";

export default function ToursPage() {
  useSEO({
    title: "All Tours & Excursions in Saint Lucia",
    description: "Browse all Saint Lucia tours and excursions - land adventures, sea excursions, thrilling activities, epic combos & kids packages. Filter by category, difficulty, and price. Book your Caribbean adventure online.",
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState(searchParams.get("category") || "all");
  const [difficulty, setDifficulty] = useState("all");
  const [priceRange, setPriceRange] = useState([0, 500]);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const params = {};
    if (category && category !== "all") params.category = category;
    if (difficulty && difficulty !== "all") params.difficulty = difficulty;
    if (priceRange[0] > 0) params.min_price = priceRange[0];
    if (priceRange[1] < 500) params.max_price = priceRange[1];
    if (search) params.search = search;

    setLoading(true);
    api.get("/tours", { params }).then(res => {
      setTours(Array.isArray(res.data) ? res.data : []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [category, difficulty, priceRange, search]);

  const clearFilters = () => {
    setCategory("all");
    setDifficulty("all");
    setPriceRange([0, 500]);
    setSearch("");
    setSearchParams({});
  };

  const hasActiveFilters = category !== "all" || difficulty !== "all" || priceRange[0] > 0 || priceRange[1] < 500 || search;

  return (
    <div className="min-h-screen bg-bone">
      <Navbar />

      {/* Header */}
      <div className="pt-28 pb-8 sm:pb-12 px-6 max-w-7xl mx-auto">
        <p className="uppercase text-xs tracking-[0.25em] text-sandy font-manrope mb-3">Our Collection</p>
        <h1 className="font-cormorant text-4xl sm:text-5xl font-light text-midnight mb-6">
          All Tours & Excursions
        </h1>

        {/* Search + Filter Toggle */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-midnight/40" />
            <Input
              data-testid="search-input"
              placeholder="Search tours..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 border-midnight/10 bg-white font-manrope text-sm"
            />
          </div>
          <button
            data-testid="toggle-filters-btn"
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 border border-midnight/10 bg-white font-manrope text-sm text-midnight hover:border-sandy transition-colors duration-200"
          >
            <SlidersHorizontal className="w-4 h-4" />
            Filters
            {hasActiveFilters && <Badge className="bg-sandy text-midnight text-xs ml-1">Active</Badge>}
          </button>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="mt-6 p-6 bg-white border border-midnight/5 grid grid-cols-1 sm:grid-cols-3 gap-6" data-testid="filter-panel">
            <div>
              <label className="font-manrope text-xs uppercase tracking-widest text-midnight/50 mb-2 block">Category</label>
              <Select value={category} onValueChange={setCategory} data-testid="category-filter">
                <SelectTrigger data-testid="category-filter-trigger" className="font-manrope text-sm">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="Land">Land</SelectItem>
                  <SelectItem value="Sea">Sea</SelectItem>
                  <SelectItem value="Adventure">Adventure</SelectItem>
                  <SelectItem value="Epic Combos">Epic Combos</SelectItem>
                  <SelectItem value="Kids Package">Kids Package</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="font-manrope text-xs uppercase tracking-widest text-midnight/50 mb-2 block">Difficulty</label>
              <Select value={difficulty} onValueChange={setDifficulty}>
                <SelectTrigger data-testid="difficulty-filter-trigger" className="font-manrope text-sm">
                  <SelectValue placeholder="All Levels" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Levels</SelectItem>
                  <SelectItem value="Easy">Easy</SelectItem>
                  <SelectItem value="Moderate">Moderate</SelectItem>
                  <SelectItem value="Hard">Hard</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="font-manrope text-xs uppercase tracking-widest text-midnight/50 mb-2 block">
                Price: ${priceRange[0]} - ${priceRange[1]}
              </label>
              <Slider
                data-testid="price-filter-slider"
                min={0} max={500} step={10}
                value={priceRange}
                onValueChange={setPriceRange}
                className="mt-4"
              />
            </div>
            {hasActiveFilters && (
              <div className="sm:col-span-3">
                <button
                  data-testid="clear-filters-btn"
                  onClick={clearFilters}
                  className="flex items-center gap-1 text-sandy font-manrope text-sm hover:underline"
                >
                  <X className="w-3 h-3" /> Clear all filters
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Tour Grid */}
      <div className="px-6 pb-24 max-w-7xl mx-auto">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1,2,3].map(i => (
              <div key={i} className="animate-pulse">
                <div className="bg-midnight/5 aspect-[4/5] mb-4" />
                <div className="h-3 bg-midnight/5 w-16 mb-2" />
                <div className="h-5 bg-midnight/5 w-3/4 mb-2" />
                <div className="h-3 bg-midnight/5 w-full" />
              </div>
            ))}
          </div>
        ) : tours.length === 0 ? (
          <div className="text-center py-20" data-testid="no-tours-message">
            <p className="font-cormorant text-2xl text-midnight/40 mb-2">No tours found</p>
            <p className="font-manrope text-sm text-midnight/40">Try adjusting your filters</p>
          </div>
        ) : (
          <>
            <p className="font-manrope text-sm text-midnight/50 mb-8" data-testid="tours-count">
              {tours.length} {tours.length === 1 ? 'tour' : 'tours'} found
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {tours.map((tour, i) => (
                <div key={tour.tour_id} className={`opacity-0 animate-fade-up stagger-${Math.min(i + 1, 6)}`}>
                  <TourCard tour={tour} />
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <Footer />
    </div>
  );
}
